import PropTypes from 'prop-types';
import { useEffect, Fragment, useState } from 'react';
import {
    Box,
    Dialog,
    DialogContent,
    DialogContentText,
    DialogTitle,
    Grid,
    Typography,
    IconButton,
    Button,
    TextField,
    Alert,
    FormHelperText
} from '@mui/material';
import { Close } from '@mui/icons-material';
import { Stack } from '@mui/system';
import AnimateButton from 'ui-component/extended/AnimateButton';
import useAuth from 'hooks/useAuth';
import CircularLoader from 'ui-component/CircularLoader';
import { dispatch } from 'store/index';
import { openSnackbar } from 'store/slices/snackbar';

export default function SessionTimeoutPassword({ toggleDialog = false }) {
    const { user, login, logout } = useAuth();
    const [open, setOpen] = useState(false);
    const [password, setPassword] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState(false);

    useEffect(() => {
        if (toggleDialog) {
            setOpen(true);

            // set timeout of for 1 min
            const reEnterPasswordTimer = setTimeout(() => {
                logout();
            }, 1000 * 60);

            // clear the timeout
            return () => {
                clearTimeout(reEnterPasswordTimer);
            };
        }
    }, [toggleDialog, logout]);

    const handleClose = (event, reason) => {
        if (reason === 'backdropClick') {
            return;
        }
        logout();
    };

    const handleSubmit = async () => {
        try {
            const { email } = user;

            setIsSubmitting(true);
            setError(false);

            // pass isRefresh since we will authenticate to continue logging
            const isRefresh = true;

            await login({ email, password, isRefresh });
            setOpen(false);
            dispatch(
                openSnackbar({
                    open: true,
                    message: 'Authentication Successfull',
                    variant: 'alert'
                })
            );
            setPassword('');
        } catch (e) {
            setError(true);
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <Fragment>
            <Dialog open={open} maxWidth="sm" keepMounted onClose={handleClose}>
                <DialogTitle>
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', width: '100%' }}>
                        <Box>
                            <Typography variant="h2" component="span" sx={{ fontWeight: 500 }}>
                                Session Timeout
                            </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <IconButton onClick={handleClose} sx={{ color: 'text.secondary' }}>
                                <Close />
                            </IconButton>
                        </Box>
                    </Box>
                </DialogTitle>
                <DialogContent component="div" className="KWA">
                    <DialogContentText component="div">
                        <Grid container spacing={2} justifyContent={'center'}>
                            <Grid item sm={12} sx={{ mb: 1 }}>
                                <Alert severity="info">Session has expired. Please re-enter you password to continue using the App.</Alert>
                            </Grid>
                            <Grid item md={12} xs={12}>
                                <TextField
                                    type="password"
                                    fullWidth
                                    label="Password"
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    error={error}
                                />
                                {error && (
                                    <Grid item xs={12}>
                                        <FormHelperText error>Invalid Password</FormHelperText>
                                    </Grid>
                                )}
                            </Grid>
                            <Grid item xs={12}>
                                <Grid container justifyContent="flex-end" sx={{ py: 1 }}>
                                    <Box display="flex" flexDirection="column">
                                        <Stack direction="row">
                                            <AnimateButton>
                                                <Button
                                                    variant="contained"
                                                    onClick={handleSubmit}
                                                    disabled={password === '' || isSubmitting}
                                                >
                                                    {isSubmitting ? (
                                                        <>
                                                            <CircularLoader size={24} minHeight="0rem" />
                                                            <Box paddingLeft={1}>Submitting</Box>
                                                        </>
                                                    ) : (
                                                        'Submit'
                                                    )}
                                                </Button>
                                            </AnimateButton>
                                        </Stack>
                                    </Box>
                                </Grid>
                            </Grid>
                        </Grid>
                    </DialogContentText>
                </DialogContent>
            </Dialog>
        </Fragment>
    );
}

SessionTimeoutPassword.propTypes = {
    toggleDialog: PropTypes.bool
};
